import { Note, removeExtension } from './note'
import { AuthType } from './User'
import { FolderIdMap } from './import/types'

// Internal FP Helpers
const PathBasedAuth = new Set([AuthType.CLOUDKIT, AuthType.CLOUDKIT_SUPABASE])

export function isPathBased(authType: AuthType): boolean {
  return PathBasedAuth.has(authType)
}

function isRoot(value: string | null | undefined): boolean {
  // eslint-disable-next-line eqeqeq, unicorn/no-null
  return value == null || value === '' || value === '/'
}

function normalizeFolderPath(path: string): string {
  if (!path || path === '/') return ''
  return path.endsWith('/') ? path.slice(0, -1) : path
}

function ensureTrailingSlash(path: string): string {
  return path.endsWith('/') ? path : path + '/'
}

function isDirectChildOfPath(filename: string, folderPath: string): boolean {
  const normalized = normalizeFolderPath(folderPath)
  if (normalized === '') return !filename.includes('/')
  const prefix = ensureTrailingSlash(normalized)
  if (!filename.startsWith(prefix)) return false
  const relative = filename.slice(prefix.length)
  return !relative.includes('/')
}

function basenameForNote(note: Note, authType: AuthType): string {
  if (isPathBased(authType) && note.filename.includes('/')) {
    const parts = note.filename.split('/')
    return parts.at(-1) ?? note.filename
  }
  return note.filename
}

function folderIdentifierForGet(
  notes: Map<string, Note>,
  folderPath: string,
  authType: AuthType,
  folderIdMap?: Map<string, string>
): string {
  const normalizedPath = normalizeFolderPath(folderPath)
  if (isPathBased(authType)) return normalizedPath
  if (normalizedPath === '') return ''
  const fromMap = folderIdMap?.get(normalizedPath)
  if (fromMap) return fromMap
  const discovered = findFolderUuid(notes, normalizedPath)
  return discovered ?? ''
}

function parentForImport(
  targetFolderPath: string,
  authType: AuthType,
  notes: Map<string, Note>,
  folderIdMap?: FolderIdMap
  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
): string | null {
  const normalized = normalizeFolderPath(targetFolderPath)
  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
  if (isRoot(normalized)) return null
  if (isPathBased(authType)) return normalized

  // Check folderIdMap first for Supabase
  const fromMap = folderIdMap?.get(normalized)
  if (fromMap) return fromMap

  return findFolderUuid(notes, normalized)
}

/**
 * Backend-agnostic folder operations for NotePlan web
 *
 * Provides unified API for folder operations that work consistently
 * across both Supabase (UUID-based) and CloudKit (path-based) backends.
 *
 * Key principles:
 * - Pure functions with no side effects
 * - O(1) lookups where possible
 * - Consistent behavior across backends
 * - Auth type context for backend-specific logic
 */

/**
 * Extracts the folder path from a note's file path
 * Example: "folder1/subfolder/note.md" -> "folder1/subfolder"
 */
export function extractFolderPath(notePath: string): string {
  // Handle trailing slash by removing it first
  const cleanPath = notePath.endsWith('/') ? notePath.slice(0, -1) : notePath
  const lastSlashIndex = cleanPath.lastIndexOf('/')
  return lastSlashIndex > 0 ? cleanPath.slice(0, lastSlashIndex) : ''
}

/**
 * Extracts just the filename from a note's file path
 * Example: "folder1/subfolder/note.md" -> "note.md"
 */
export function extractFileName(notePath: string): string {
  const lastSlashIndex = notePath.lastIndexOf('/')
  return lastSlashIndex >= 0 ? notePath.slice(lastSlashIndex + 1) : notePath
}

/**
 * Gets all notes in a specific folder, handling backend differences
 * For path-based backends (CloudKit, CLOUDKIT_SUPABASE): Uses path prefix matching
 * For UUID-based backends (Supabase): Uses parent UUID matching
 */
export function getNotesInFolder(
  notes: Map<string, Note>,
  folderPathOrUuid: string,
  authType: AuthType
): Note[] {
  const results: Note[] = []

  if (isPathBased(authType)) {
    const folderPath = normalizeFolderPath(folderPathOrUuid)
    for (const note of notes.values()) {
      if (note.isFolder) continue
      if (isDirectChildOfPath(note.filename, folderPath)) {
        results.push(note)
      }
    }
    return results
  }

  // Supabase UUID-based
  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
  const targetParent = isRoot(folderPathOrUuid) ? null : folderPathOrUuid
  for (const note of notes.values()) {
    if (note.isFolder) continue
    // eslint-disable-next-line eqeqeq -- Intentional loose comparison for null/undefined
    if (note.parent == targetParent) {
      results.push(note)
    }
  }

  return results
}

/**
 * Helper function to check if a note is in trash by checking if it's a descendant of @Trash folder
 */
function isInTrash(note: Note, notes: Map<string, Note>): boolean {
  let current: Note | undefined = note
  while (current) {
    if (current.filename === '@Trash' && current.isFolder) {
      return true
    }
    if (!current.parent) break
    current = notes.get(current.parent)
  }
  return false
}

/**
 * Finds folder UUID by path for Supabase backend
 * Traverses the folder hierarchy step by step to find the exact folder at the specified path.
 * Returns null if the folder doesn't exist, indicating it should be created.
 *
 * Enhanced to properly handle cases where multiple folders with the same name exist
 * by ensuring we traverse the exact hierarchy path and validate the result.
 * Also excludes folders that are in the trash.
 */
export function findFolderUuid(
  notes: Map<string, Note>,
  targetFolderPath: string
  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
): string | null {
  const path = normalizeFolderPath(targetFolderPath)
  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
  if (isRoot(path)) return null

  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
  let currentParent: string | null = null
  let traversedPath = ''

  for (const segment of path.split('/')) {
    traversedPath = traversedPath ? `${traversedPath}/${segment}` : segment

    const candidates: Note[] = []
    for (const note of notes.values()) {
      // eslint-disable-next-line eqeqeq -- Intentional loose comparison for null/undefined
      const parentMatches = note.parent == currentParent
      // Check folder matches and exclude folders that are in the trash
      if (note.isFolder && note.filename === segment && parentMatches && !isInTrash(note, notes)) {
        candidates.push(note)
      }
    }

    // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
    if (candidates.length === 0) return null

    if (candidates.length > 1) {
      candidates.sort((a, b) =>
        (b.recordName ?? '').localeCompare(a.recordName ?? '')
      )
    }

    // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
    currentParent = candidates[0].recordName ?? null
  }

  // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
  return traversedPath === path && currentParent != null ? currentParent : null
}

/**
 * Resolves filename and parent for import based on backend architecture
 */
export function resolveImportLocation(
  notePath: string,
  authType: AuthType,
  folderIdMap: FolderIdMap,
  targetFolder: string,
  notes: Map<string, Note>
): { filename: string; parent: string | null } {
  const folderPath = normalizeFolderPath(extractFolderPath(notePath))
  const fileName = extractFileName(notePath)

  if (isPathBased(authType)) {
    // For CloudKit, filename includes the full path
    // If targetFolder is specified, prepend it to the path
    const normalizedTarget = normalizeFolderPath(targetFolder)
    const fullFilename = normalizedTarget
      ? `${normalizedTarget}/${notePath}`
      : notePath

    // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
    return { filename: fullFilename, parent: null }
  }

  // Supabase: parent is UUID
  if (folderPath) {
    const fromMap = folderIdMap.get(folderPath)
    if (fromMap) {
      return { filename: fileName, parent: fromMap }
    }
    const discovered = findFolderUuid(notes, folderPath)
    if (discovered) {
      return { filename: fileName, parent: discovered }
    }
  }

  const parent = parentForImport(targetFolder, authType, notes, folderIdMap)
  return { filename: fileName, parent }
}

// Backward compatibility wrapper - maintains old parameter order
export function resolveImportFileReference(
  notePath: string,
  folderIdMap: FolderIdMap,
  targetFolder: string,
  notes: Map<string, Note>,
  authType: AuthType
): { filename: string; parent: string | null } {
  return resolveImportLocation(notePath, authType, folderIdMap, targetFolder, notes)
}

/**
 * Builds appropriate parent reference for target folder, handling backend differences
 * For CLOUDKIT_SUPABASE: Uses CloudKit behavior (path-based) for private notes,
 * which is the case for imports since they create PROJECT_NOTE types.
 */
export function buildParentReference(
  targetFolderPath: string,
  notes: Map<string, Note>,
  authType: AuthType
): string | null {
  // For root folder, return null for both backends
  if (
    !targetFolderPath ||
    targetFolderPath === '/' ||
    targetFolderPath === ''
  ) {
    // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
    return null
  }

  if (authType === AuthType.SUPABASE) {
    return findFolderUuid(notes, targetFolderPath)
  }

  // For CloudKit and CLOUDKIT_SUPABASE (private notes), return the folder path directly
  return targetFolderPath
}

/**
 * Gets the full path of a note, handling backend differences
 * For CloudKit and CLOUDKIT_SUPABASE (private notes): returns filename directly (already contains full path)
 * For Supabase and CLOUDKIT_SUPABASE (team notes): builds path by traversing parent chain
 */
export function getNotePath(
  note: Note,
  authType: AuthType,
  notesMap?: Map<string, Note>
): string {
  if (isPathBased(authType) || !notesMap) return note.filename

  const parts: string[] = []
  let current: Note | undefined = note
  while (current) {
    parts.unshift(current.filename)
    if (!current.parent) break
    current = notesMap.get(current.parent)
  }
  return parts.join('/')
}

/**
 * Finds a note in a specific folder by filename
 * Uses backend-agnostic helpers for unified logic across CloudKit and Supabase
 */
export function findNoteInFolder(
  notes: Map<string, Note>,
  filename: string,
  folderPath: string,
  authType: AuthType
): Note | undefined {
  const folderIdentifier = isPathBased(authType)
    ? normalizeFolderPath(folderPath)
    : isRoot(folderPath)
      ? ''
      : folderPath

  const inFolder = getNotesInFolder(notes, folderIdentifier, authType)
  const targetBase = removeExtension(filename)

  for (const note of inFolder) {
    const name = basenameForNote(note, authType)
    if (name === filename) return note
    if (removeExtension(name) === targetBase) return note
  }

  return undefined
}

/**
 * Creates a filename map scoped to a specific folder
 * Groups notes by their basename (filename without extension)
 * Only includes notes that are directly in the specified folder
 */
export function createFolderScopedFilenameMap(
  notes: Map<string, Note>,
  folderPath: string,
  authType: AuthType,
  folderIdMap?: Map<string, string>
): Map<string, Note[]> {
  const out = new Map<string, Note[]>()
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Defensive check for runtime safety
  if (!notes) return out

  const identifier = folderIdentifierForGet(
    notes,
    folderPath,
    authType,
    folderIdMap
  )
  const folderNotes = getNotesInFolder(notes, identifier, authType)

  for (const note of folderNotes) {
    const nameForBasename = basenameForNote(note, authType)
    const base = removeExtension(nameForBasename, note.isFolder)
    const bucket = out.get(base) ?? []
    bucket.push(note)
    out.set(base, bucket)
  }

  return out
}
